<template>
    <CollapsibleWidget class="editable-file-item" :is-opened="isOpen" collapsed-height="47" @onChange="toggleOpen">
        <template #name>{{ editable.label }}</template>

        <template #controls>
            <EditableRestrictionControl v-if="showRestrictionControl" :editable="editable" />
        </template>

        <div class="editable-widget__body">
            <div v-if="readOnly" class="editable-widget__body--readonly" title="Read Only" @click.stop.prevent></div>
            <EditableFileListView
                :editable="editable"
                :groups-affecting-editable="groupsAffectingEditable"
                :is-opened="isOpen"
                @update="updateFileEditable"
            />
        </div>
    </CollapsibleWidget>
</template>

<script lang="ts">
import { defineComponent, toRef, ref, type PropType } from "vue";
import type { Editable } from "shared-types";

import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import EditableFileListView from "@/components/Campaign/Editables/EditableFileListView.vue";
import EditableRestrictionControl from "@/components/Campaign/Editables/EditableRestrictionControl.vue";

import { useToggleEditableItem } from "@/composables/useToggleEditableItem";
import { useEditableFile } from "@/composables/useEditableFile";

export default defineComponent({
    name: "EditableFileWidget",
    components: {
        EditableRestrictionControl,
        EditableFileListView,
        CollapsibleWidget
    },

    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },

        allExpanded: {
            type: Boolean,
            default: true
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        showRestrictionControl: {
            type: Boolean,
            default: true
        }
    },

    setup(props, { emit }) {
        const allExpanded = toRef(props, "allExpanded");
        const editable = toRef(props, "editable");
        const groupsAffectingEditable = toRef(props, "groupsAffectingEditable");
        const type = ref(editable.value.type);

        const { isOpen, toggleOpen } = useToggleEditableItem({ allExpanded });
        const { updateFileEditable } = useEditableFile({ emit, editable, groupsAffectingEditable, type });

        return {
            isOpen,
            toggleOpen,
            updateFileEditable
        };
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.editable-file-item .widget-mode {
    color: $cmp-dark-disabled-color;
}

.editable-file-item .widget-mode--active {
    color: $cmp-dark-font-color;
}
</style>
